// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — colour system + WCAG contrast helper (principle #22). The play surface is a deep
// indigo mountain-night on which the cairn's stones are drawn. The colours that carry MEANING
// and must clear the 3:1 graphic floor against every surface they sit on are: the STONE face
// (its rune ink on top), the LEGAL ring (a stone that can be taken now), the HAND accent, the
// CLEAR glow (a cairn cleared) and the BURY flash (out of draws). Colour is never the only cue:
// the gameplay identity of a stone is its rune LABEL (A–K), never its hue; the four stone hues
// are redundant decoration, a legal stone also carries a ring + lift, a cleared cairn flashes
// green, a bury shakes. Every meaningful colour is pinned by contrast.test.ts.

export const PALETTE = {
  // board + panels
  bg: '#171226',
  bgDeep: '#0f0c1c',
  board: '#171226', // the play surface — stones are drawn on this
  panel: '#241d3d',
  panelDeep: '#140f24',
  edge: '#3a2f5c',

  // text
  text: '#f2eefb',
  textMuted: '#bcb2da',
  ink: '#2a1c0a', // dark rune ink drawn ON a light stone

  // the four cosmetic stone hues (decorative — identity is the rune LABEL, not the hue)
  stone0: '#e9cd93',
  stone1: '#e3c7a6',
  stone2: '#e9c6b4',
  stone3: '#dcd0a0',

  // play-surface roles (each also has a shape/behaviour cue)
  legal: '#ffcf5a', // the ring on a stone that can be taken now
  hand: '#55d8e8', // the hand stone / keyboard cursor accent
  clear: '#79e6a6', // the whole cairn when it is cleared
  bury: '#ff7d6d', // the flash when the draws run out (Scree)
  spark: '#ffb454', // heat sparks
} as const;

/** The four decorative stone hues, indexed. */
export const STONE_HUES = [PALETTE.stone0, PALETTE.stone1, PALETTE.stone2, PALETTE.stone3] as const;

/* ── WCAG contrast ────────────────────────────────────────────────────────── */

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Relative luminance of a #rrggbb colour (WCAG 2.1). */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a hex colour: ${hex}`);
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => channel(parseInt(h, 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #rrggbb colours (1..21, symmetric). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
